import React from 'react';
import {
  Avatar,
  Box,
  Typography,
  Button,
  Chip,
  Slider,
  Paper
} from '@mui/material';
import {
  Battery3Bar,
  Settings,
  BatteryChargingFull,
  DirectionsCar
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

const VehicleSelection = ({
  selectedVehicle,
  batteryPercentage,
  setBatteryPercentage,
  onVehicleSelection
}) => {
  const theme = useTheme();

  const getBatteryTokens = (percentage) => {
    if (percentage >= 60) {
      return {
        color: theme.palette.primary.main,
        gradient: theme.palette.gradients.brand,
        soft: alpha(theme.palette.primary.main, 0.18),
        shadow: alpha(theme.palette.primary.main, 0.3)
      };
    }
    if (percentage >= 30) {
      return {
        color: theme.palette.warning.main,
        gradient: theme.palette.gradients.warning,
        soft: alpha(theme.palette.warning.main, 0.22),
        shadow: alpha(theme.palette.warning.main, 0.28)
      };
    }
    return {
      color: theme.palette.error.main,
      gradient: theme.palette.gradients.error,
      soft: alpha(theme.palette.error.main, 0.24),
      shadow: alpha(theme.palette.error.main, 0.35)
    };
  };

  const getBrandInitials = (name) => {
    if (!name) return 'EV';
    return name
      .split(' ')
      .filter(Boolean)
      .map((word) => word[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const getBatteryIcon = (percentage) => {
    if (percentage >= 60) return <BatteryChargingFull />;
    return <Battery3Bar />;
  };

  const batteryTokens = getBatteryTokens(batteryPercentage);
  const vehicleBrandLogo = selectedVehicle?.brandLogo;
  const vehicleBrandColor = selectedVehicle?.brandColor || theme.palette.primary.main;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: theme.palette.text.primary }}>
        Vehicle & Battery
      </Typography>

      <Button
        fullWidth
        variant="outlined"
        onClick={onVehicleSelection}
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          borderColor: alpha(theme.palette.primary.main, 0.25),
          justifyContent: 'flex-start',
          textTransform: 'none',
          color: theme.palette.text.primary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08)
          }
        }}
        startIcon={
          <Avatar
            variant="rounded"
            src={vehicleBrandLogo || undefined}
            alt={selectedVehicle?.brand || 'EV brand'}
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              backgroundColor: vehicleBrandLogo
                ? alpha(theme.palette.primary.main, 0.08)
                : alpha(vehicleBrandColor, 0.15),
              color: theme.palette.primary.main
            }}
          >
            {!vehicleBrandLogo && <DirectionsCar fontSize="small" />}
          </Avatar>
        }
        endIcon={<Settings />}
      >
        <Box sx={{ flex: 1, textAlign: 'left' }}>
          {selectedVehicle ? (
            <Box>
              <Typography variant="body1" fontWeight={500}>
                {selectedVehicle.brand} {selectedVehicle.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedVehicle.batteryCapacity}kWh {selectedVehicle.range}km range
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Select Your Vehicle
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Choose your electric vehicle
              </Typography>
            </Box>
          )}
        </Box>
      </Button>

      {selectedVehicle && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.divider, 0.7)}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar
              variant="rounded"
              src={vehicleBrandLogo || undefined}
              alt={selectedVehicle?.brand}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                backgroundColor: vehicleBrandLogo
                  ? alpha(vehicleBrandColor, 0.08)
                  : alpha(vehicleBrandColor, 0.15),
                color: vehicleBrandColor,
                fontWeight: 600
              }}
            >
              {!vehicleBrandLogo && getBrandInitials(selectedVehicle?.brand)}
            </Avatar>
            <Box sx={{ color: batteryTokens.color, display: 'flex', alignItems: 'center' }}>
              {getBatteryIcon(batteryPercentage)}
            </Box>
            <Typography variant="body2" fontWeight={500}>
              Current Battery: {batteryPercentage}%
            </Typography>
            <Chip
              label={`${Math.round((batteryPercentage / 100) * selectedVehicle.batteryCapacity)}kWh`}
              size="small"
              sx={{
                ml: 'auto',
                backgroundColor: batteryTokens.soft,
                color: batteryTokens.color,
                fontSize: '0.75rem'
              }}
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <Slider
              value={batteryPercentage}
              onChange={(e, value) => setBatteryPercentage(value)}
              min={5}
              max={100}
              step={1}
              sx={{
                color: batteryTokens.color,
                '& .MuiSlider-thumb': {
                  backgroundColor: batteryTokens.color,
                  border: '2px solid white',
                  boxShadow: `0 2px 10px ${batteryTokens.shadow}`,
                  width: 20,
                  height: 20,
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: `0 0 0 8px ${batteryTokens.soft}`
                  }
                },
                '& .MuiSlider-track': {
                  backgroundImage: batteryTokens.gradient,
                  backgroundColor: batteryTokens.color,
                  border: 'none',
                  height: 8
                },
                '& .MuiSlider-rail': {
                  backgroundColor: alpha(theme.palette.text.primary, 0.08),
                  height: 8
                }
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {Math.round((batteryPercentage / 100) * selectedVehicle.range)} km available
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedVehicle.range} km max
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default VehicleSelection;
